import React, { useEffect, useState, useMemo } from "react";
import { Constants } from "@eagerdog/constants";
import { IScore, IBarnHuntScore } from "@eagerdog/interfaces";

import Dropdown, { IOption } from "src/components/Dropdown/Dropdown";
import Input from "src/components/Input/Input";
import StopWatch from "src/components/EnterResults/StopWatch/StopWatch";

interface IProps {
  result: IScore;
  onChange(result: IBarnHuntScore): void;
}

/**
 * Crazy 8's Results Form
 * - Score (integer, manual point entry)
 * - Time (optional, defaults to 2:00 if not entered)
 * - Time bonus: +10 if time <= 1:30, +5 if time <= 1:45
 * - NQ if no rats or negative score
 * - No official placement, but sorted descending by score, then ascending by time
 */
const Crazy8sForm: React.FC<IProps> = (props) => {
  const [score, setScore] = useState<string>("");
  const [time, setTime] = useState<string>("2:00:00");
  const [result, setResult] = useState<IOption>({ id: Constants.score_result_type.qualified, value: "Qualified" });
  const [loaded, setLoaded] = useState<boolean>(false);
  const [hasChanged, setHasChanged] = useState<boolean>(false);

  const resultOptions: IOption[] = useMemo(
    () => [
      { id: Constants.score_result_type.qualified, value: "Qualified" },
      { id: Constants.score_result_type.not_qualified, value: "NQ" },
      { id: Constants.score_result_type.absent, value: "Absent" },
    ],
    []
  );

  useEffect(() => {
    if (!loaded) {
      if (props.result && props.result.barn_hunt) {
        const { result_type, crazy8_score: savedScore, time: savedTime } = props.result.barn_hunt;
        
        for (let ro in resultOptions) {
          if (resultOptions[ro].id === result_type) {
            setResult(resultOptions[ro]);
            break;
          }
        }

        setScore(savedScore !== undefined ? savedScore.toString() : "");
        if (savedTime) {
          setTime(savedTime);
        }
      }
      setLoaded(true);
    }
  }, [loaded, props.result, resultOptions]);

  // Calculate time bonus
  const timeBonus = useMemo(() => {
    if (!time) return 0;

    const parts = time.split(":");
    if (parts.length !== 3) return 0;

    const minutes = parseInt(parts[0], 10);
    const seconds = parseFloat(parts[1]);

    if (isNaN(minutes) || isNaN(seconds)) return 0;

    const totalSeconds = Math.floor(minutes * 60 + seconds);

    if (totalSeconds <= 90) return 10;  // 1:30
    if (totalSeconds <= 105) return 5;  // 1:45
    return 0;
  }, [time]);

  // Calculate total score
  const totalScore = useMemo(() => {
    const baseScore = parseInt(score, 10) || 0;
    return baseScore + timeBonus;
  }, [score, timeBonus]);

  useEffect(() => {
    if (hasChanged) {
      const scoreNum = parseInt(score, 10);
      
      // Convert from M:SS:00 to M:SS format for storage (drop the hundredths)
      let resultTime: string | undefined;
      const parts = time.split(":");
      if (parts.length === 3) {
        resultTime = `${parts[0]}:${parts[1]}`;
      }
      
      props.onChange({
        result_type: result.id,
        crazy8_score: !isNaN(scoreNum) ? scoreNum : undefined,
        time: resultTime || "2:00",
        crazy8_bonus: timeBonus,
      });

      setHasChanged(false);
    }
  }, [hasChanged, score, time, timeBonus, props, result.id]);

  const handleScoreChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setScore(event.target.value);
    setHasChanged(true);
  };

  const handleTimeChange = (value: string) => {
    setTime(value);
    setHasChanged(true);
  };

  const handleResultChange = (e: any, value: IOption) => {
    setResult(value);
    setHasChanged(true);
  };

  return (
    <div className="BarnHuntResults">
      <div className="instruction-section">
        <p className="instruction-text">
          <strong>Crazy 8's:</strong> Enter score (points) and time.
          <br />
          Bonus: +10 if time ≤ 1:30, +5 if time ≤ 1:45
        </p>
      </div>

      <div className="form-row">
        <Dropdown
          value={result}
          label="Result"
          onChange={handleResultChange}
          options={resultOptions}
          placeholder=""
        />
      </div>

      {result.id === Constants.score_result_type.qualified && (
        <>
          <div className="form-row">
            <Input
              type="number"
              onChange={handleScoreChange}
              label="Score (Points)"
              placeholder="Enter score"
              value={score}
            />
          </div>

          <div className="form-row">
            <StopWatch
              time={time}
              onChange={handleTimeChange}
              label="Time (default 2:00)"
              hideTT={true}
            />
          </div>

          {score && (
            <div className="result-display">
              <div className="display-item">
                <label>Base Score:</label>
                <div className="display-value">{score}</div>
              </div>
              <div className="display-item">
                <label>Time Bonus:</label>
                <div className="display-value">+{timeBonus}</div>
              </div>
              <div className="display-item total">
                <label>Total Score:</label>
                <div className="display-value">{totalScore}</div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Crazy8sForm;

